import {
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { faComment as faCommentRegular } from "@fortawesome/free-regular-svg-icons";
import { faComment as faCommentSolid } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { TrainingVerifyService } from "../training-verify.service";
import { ActivatedRoute, Router } from "@angular/router";

// Interfaces for the new JSON data structure
interface TraineeAnswer {
  quiz_question_id: number;
  question_text: string;
  level: string;
  answer_text: string;
  score: number;
}

interface Topics {
  [key: string]: {
    pre: TraineeAnswer[];
    post?: TraineeAnswer[];
    plj2?: TraineeAnswer[];
  };
}

interface TraineeData {
  trainee_name: string;
  trainee_id: number;
  first_name: string;
  last_name: string;
  metadata: {
    individual_quotient_pre: number;
    individual_quotient_post: number;
    individual_quotient_change: number;
  };
  topics: Topics;
}

interface TopicHeader {
  key: string;
  fullQuestion: string;
}

interface ScoreOption {
  quiz_question_answer_id: number;
  label: string;
  class: string;
}

interface QuizItem {
  quiz_trainee_id: number;
  quiz_id: number;
  quiz_question_id: number;
  question_text: string;
  topic_name: string;
  level: string;
  answer_text: string;
  score: number;
  manager_level: string | null;
}

interface TopicData {
  pre: QuizItem[];
}

interface Trainee {
  trainee_name: string;
  trainee_id: number;
  first_name: string;
  last_name: string;
  topics: Record<string, TopicData>;
}

@Component({
  selector: "app-training-verify",
  templateUrl: "./training-verify.component.html",
  styleUrls: ["./training-verify.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class TrainingVerifyComponent implements OnInit {
  faCommentRegular = faCommentRegular;
  faCommentSolid = faCommentSolid;

  commentsMap = new Map<string, boolean>();

  supervisorScores = new Map<string, ScoreOption>();

  quizTopics: TopicHeader[] = [];
  quizData: any = {};

  trainingId!: number;
  topics: any[] = [];

  trainingTitle: string = "";
  trainingCode: string = "";

  savingStatus = new Map<string, boolean>();

  isSubmitted = false; // Track if verification has been submitted

  constructor(
    private cdRef: ChangeDetectorRef,
    private trainingVerifyService: TrainingVerifyService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  currentStage: "pre" | "post" | "plj2" = "pre";

  ngOnInit(): void {
    this.route.parent?.params.subscribe((params) => {
      this.trainingId = +params["training_id"];

      // Determine current stage from last URL segment
      const lastSegment =
        this.route.snapshot.url[this.route.snapshot.url.length - 1]?.path;
      if (["pre", "post", "plj2"].includes(lastSegment)) {
        this.currentStage = lastSegment as "pre" | "post" | "plj2";
      }

      this.loadTopics();
    });
  }

  showTrainingNotFound = false;

  loadTopics() {
    this.trainingVerifyService.getTrainingVerifyData(this.trainingId).subscribe(
      (res) => {
        if (res.status === "success" && res.data?.length) {
          this.quizData = res;

          // ✅ Capture training title & code from metadata
          this.trainingTitle = res.metadata?.training_title || "Training";
          this.trainingCode = res.metadata?.training_code || "";

          // Initialize topics
          if (res.metadata?.topics?.length) {
            this.quizTopics = res.metadata.topics.map((t: any) => ({
              key: t.topic_code,
              fullQuestion: t.topic,
            }));
          } else {
            const firstTrainee = res.data[0];
            this.quizTopics = Object.keys(firstTrainee.topics).map((key) => ({
              key,
              fullQuestion:
                firstTrainee.topics[key]?.pre?.[0]?.question_text || "",
            }));
          }

          this.initializeSupervisorScores();
          this.initializeCommentsFromApi();
          
          // Check if verification has already been submitted
          this.isSubmitted = res.metadata?.is_submitted === true || res.metadata?.submitted === true || false;

          this.showTrainingNotFound = false;
        } else {
          this.showTrainingNotFound = true;
          this.quizData = { data: [] };
          this.quizTopics = [];
          this.trainingTitle = "";
          this.trainingCode = "";
        }

        this.cdRef.detectChanges();
      },
      (err) => {
        console.error("API error", err);
        this.showTrainingNotFound = true;
        this.quizData = { data: [] };
        this.quizTopics = [];
        this.trainingTitle = "";
        this.trainingCode = "";
        this.cdRef.detectChanges();
      }
    );
  }

  initializeSupervisorScores(): void {
    const loggedInTraineeId = this.getLoggedInTraineeId();

    this.quizData.data.forEach((trainee: any) => {
      const traineeId = trainee.trainee_id;

      this.quizTopics.forEach((topic: any) => {
        const quizQuestion =
          trainee.topics?.[topic.key]?.[this.currentStage]?.[0];
        if (!quizQuestion) return;

        const managerEntry =
          quizQuestion.manager_level?.[loggedInTraineeId]?.[0];
        if (managerEntry) {
          this.supervisorScores.set(`${traineeId}-${topic.key}`, {
            quiz_question_answer_id: managerEntry.manager_score,
            label: this.toTitleCase(managerEntry.level),
            class: this.getColorClass(managerEntry.level),
          });
        }
      });
    });
  }

  private initializeCommentsFromApi(): void {
    if (!this.quizData?.data) return;

    this.quizData.data.forEach((trainee: any) => {
      Object.keys(trainee.topics).forEach((topicKey) => {
        const stageData = trainee.topics[topicKey]?.[this.currentStage]?.[0];
        if (!stageData) return;

        const loggedInTraineeId = this.getLoggedInTraineeId();
        const managerComment =
          stageData.manager_level?.[loggedInTraineeId]?.[0]?.manager_comment ??
          "";

        if (managerComment.trim() !== "") {
          this.commentsMap.set(`${trainee.trainee_id}-${topicKey}`, true);
        } else {
          this.commentsMap.set(`${trainee.trainee_id}-${topicKey}`, false);
        }
      });
    });

    this.cdRef.detectChanges();
  }

  getColorClass(level: string): string {
    switch (level.toLowerCase()) {
      case "expert":
        return "dark-green";
      case "advanced":
        return "light-green";
      case "intermediate":
        return "light-red";
      case "basic":
        return "dark-red";
      default:
        return "";
    }
  }

  /**
   * A getter to check if any of the scores are currently being saved.
   * This is used for the overall loading spinner.
   */
  get isSaving(): boolean {
    return Array.from(this.savingStatus.values()).some((isSaving) => isSaving);
  }

  /**
   * Checks if a specific trainee's score for a given topic is currently being saved.
   * @param traineeId The ID of the trainee.
   * @param topicKey The key of the topic (e.g., 'PIP01').
   * @returns True if the score is being saved, false otherwise.
   */
  isSavingForTraineeAndTopic(traineeId: number, topicKey: string): boolean {
    return this.savingStatus.get(`${traineeId}-${topicKey}`) || false;
  }

  /**
   * Handles the change event from the supervisor's dropdown for a specific trainee and topic.
   * @param traineeId The ID of the trainee whose score is being changed.
   * @param topicKey The key of the topic for which the score is being changed.
   * @param newScore The new score selected by the supervisor.
   */

  getLoggedInTraineeId(): number {
    const traineeRaw = localStorage.getItem("trainee");
    if (!traineeRaw) return 0;
    try {
      return JSON.parse(traineeRaw).trainee_id || 0;
    } catch {
      return 0;
    }
  }

  onSupervisorScoreChange(
    traineeId: number,
    topicKey: string,
    selectedOptionId: number
  ): void {
    if (!selectedOptionId) return;
    
    // Prevent changes if already submitted
    if (this.isSubmitted) return;

    const compositeKey = `${traineeId}-${topicKey}`;

    const trainee = this.quizData.data.find((t) => t.trainee_id === traineeId);
    if (!trainee) return;

    const quizQuestion = trainee.topics?.[topicKey]?.[this.currentStage]?.[0];
    if (!quizQuestion) return;

    const selectedOption = (quizQuestion.answer_level ?? []).find(
      (opt) => opt.quiz_question_answer_id === selectedOptionId
    );
    if (!selectedOption) return;

    const levelStr = this.toTitleCase(selectedOption.level);

    // Update supervisorScores map for UI (local only, not saved to backend yet)
    this.supervisorScores.set(compositeKey, {
      quiz_question_answer_id: selectedOptionId,
      label: levelStr,
      class: this.getColorClass(levelStr),
    });

    // Update manager_level only for logged-in trainee (local only)
    const loggedInTraineeId = this.getLoggedInTraineeId();
    if (!quizQuestion.manager_level) quizQuestion.manager_level = {};
    if (!quizQuestion.manager_level[loggedInTraineeId]) {
      quizQuestion.manager_level[loggedInTraineeId] = [
        { level: null, manager_score: null, manager_comment: null },
      ];
    }

    quizQuestion.manager_level[loggedInTraineeId][0] = {
      ...quizQuestion.manager_level[loggedInTraineeId][0],
      level: levelStr,
      manager_score: selectedOptionId,
      // keep existing comment untouched
    };

    quizQuestion.manager_score = selectedOptionId;

    // Trigger UI update (no backend save - only save on submit)
    this.quizData = { ...this.quizData };
    this.cdRef.detectChanges();
  }

  private toTitleCase(str: string): string {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  getButtonClass(traineeId: number, topicKey: string): string {
    const option = this.supervisorScores.get(`${traineeId}-${topicKey}`);
    if (option) {
      return option.class;
    }

    const trainee = this.quizData.data.find(
      (t: any) => t.trainee_id === traineeId
    );
    if (trainee) {
      const quizQuestion = trainee.topics?.[topicKey]?.pre?.[0];
      if (quizQuestion?.manager_level) {
        return this.getColorClass(quizQuestion.manager_level);
      }

      if (quizQuestion?.quiz_question_answer_level) {
        return this.getColorClass(quizQuestion.quiz_question_answer_level);
      }
    }

    return "";
  }

  openCommentPopup(traineeId: number, topicKey: string): void {
    const stage = this.currentStage || "pre";
    const quizQuestion = this.quizData.data.find(
      (t: any) => t.trainee_id === traineeId
    )?.topics[topicKey]?.[stage]?.[0];

    if (!quizQuestion) return;

    const loggedInTraineeId = this.getLoggedInTraineeId();

    // Get existing comment for logged-in trainee only
    const existingComment =
      quizQuestion.manager_level?.[loggedInTraineeId]?.[0]?.manager_comment ??
      "";

    Swal.fire({
      title: "Add Comments",
      input: "textarea",
      inputPlaceholder: "Type your comment here...",
      inputValue: existingComment,
      showCancelButton: true,
      confirmButtonText: "Add Comments",
      cancelButtonText: "Cancel",
      inputAttributes: { "aria-label": "Type your comment here" },
    }).then((result) => {
      if (result.isConfirmed) {
        const comment = result.value ?? "";

        // Ensure manager_level array exists for this trainee
        if (!quizQuestion.manager_level) quizQuestion.manager_level = {};
        if (!quizQuestion.manager_level[loggedInTraineeId]) {
          quizQuestion.manager_level[loggedInTraineeId] = [
            { level: null, manager_score: null, manager_comment: null },
          ];
        }

        quizQuestion.manager_level[loggedInTraineeId][0] = {
          ...quizQuestion.manager_level[loggedInTraineeId][0],
          manager_comment: comment,
        };

        // Update commentsMap for UI
        this.commentsMap.set(`${traineeId}-${topicKey}`, !!comment);

        // Trigger UI update
        this.quizData = { ...this.quizData };
        this.cdRef.detectChanges();

        // Send comment to backend
        this.trainingVerifyService
          .saveCommentTrainingVerifyData({
            quiz_trainee_id: quizQuestion.quiz_trainee_id,
            comment,
          })
          .subscribe({
            next: () => {
              Swal.fire("Saved!", "Your comment has been added.", "success");
            },
            error: (err) => {
              console.error("Save comment error", err);
              Swal.fire("Error!", "Could not save comment.", "error");
            },
          });
      }
    });
  }

  hasComment(traineeId: number, topicKey: string): boolean {
    return this.commentsMap.get(`${traineeId}-${topicKey}`) ?? false;
  }

  addComment(traineeId: number, topicKey: string, comment: string) {
    this.commentsMap.set(`${traineeId}-${topicKey}`, true);

    this.cdRef.detectChanges();
  }

  getAnswerOptions(trainee: any, topicKey: string): ScoreOption[] {
    const answers =
      trainee.topics?.[topicKey]?.[this.currentStage]?.[0]?.answer_level ?? [];
    return answers.map((l: any) => ({
      quiz_question_answer_id: l.quiz_question_answer_id,
      label: l.level,
      class: this.getColorClass(l.level),
    }));
  }

  getColorClassByAnswerId(traineeId: number, topicKey: string): string {
    const key = `${traineeId}-${topicKey}`;
    const score = this.supervisorScores.get(key);
    return score?.class || "";
  }

  getAnswerLabelById(traineeId: number, topicKey: string): string {
    const option = this.supervisorScores.get(`${traineeId}-${topicKey}`);
    return option ? option.label : "";
  }

  submitTrainingVerify(): void {
    if (!this.quizData?.data || this.quizData.data.length === 0) return;
    
    // Prevent multiple submissions
    if (this.isSubmitted) {
      Swal.fire("Info", "Verification has already been submitted.", "info");
      return;
    }

    const traineeFromStorage = localStorage.getItem("trainee");
    if (!traineeFromStorage) {
      Swal.fire("Error!", "Trainee info not found in localStorage.", "error");
      return;
    }
    const traineeObj = JSON.parse(traineeFromStorage);
    const traineeIdManagerVerify = traineeObj.trainee_id;

    const trainees: Trainee[] = this.quizData.data as Trainee[];
    const stage = this.currentStage || "pre";

    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to submit the ${stage.toUpperCase()} training verification?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Submit",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        // Save all supervisor selections to backend before submitting
        this.saveAllSupervisorSelections().then(() => {
          let courseTaskId: number | null = null;
          for (const trainee of trainees) {
            for (const topicKey of Object.keys(trainee.topics)) {
              const quizItems = trainee.topics[topicKey]?.[stage];
              if (quizItems && quizItems.length > 0) {
                courseTaskId = quizItems[0].course_task_id;
                break;
              }
            }
            if (courseTaskId) break;
          }

          if (!courseTaskId) {
            Swal.fire(
              "Error!",
              `No quiz items found for stage: ${stage}`,
              "error"
            );
            return;
          }

          const body = {
            training_id: this.trainingId,
            course_task_id: courseTaskId,
            trainee_id_manager_verify: traineeIdManagerVerify,
          };

        this.trainingVerifyService.submitTrainingVerifyData(body).subscribe({
          next: (res: any) => {
            if (res.status === "success") {
              // Mark as submitted
              this.isSubmitted = true;
              this.cdRef.detectChanges();
              
              Swal.fire(
                "Submitted!",
                "All records are verified successfully.",
                "success"
              ).then(() => {
                // Navigate to dashboard after user clicks OK
                this.router.navigate(["/dashboard"]);
              });
            } else if (res.status === "failure") {
              Swal.fire(
                "Missing verifications",
                "Please verify all the participant responses",
                "warning"
              );
            } else {
              Swal.fire("Error!", "Unexpected response from server.", "error");
            }
          },
            error: (err) => {
              console.error("Submit error", err);
              Swal.fire(
                "Error!",
                "Could not submit training verification.",
                "error"
              );
            },
          });
        });
      }
    });
  }

  // Save all supervisor selections to backend
  private saveAllSupervisorSelections(): Promise<void> {
    return new Promise((resolve, reject) => {
      const loggedInTraineeId = this.getLoggedInTraineeId();
      const savePromises: Promise<any>[] = [];

      this.quizData.data.forEach((trainee: any) => {
        const traineeId = trainee.trainee_id;
        this.quizTopics.forEach((topic: any) => {
          const supervisorScore = this.supervisorScores.get(`${traineeId}-${topic.key}`);
          if (supervisorScore) {
            const quizQuestion = trainee.topics?.[topic.key]?.[this.currentStage]?.[0];
            if (quizQuestion) {
              const body = {
                quiz_trainee_id: quizQuestion.quiz_trainee_id,
                trainee_id_manager_verify: loggedInTraineeId,
                quiz_question_answer_id: supervisorScore.quiz_question_answer_id,
                quiz_question_answer_level: supervisorScore.label,
              };

              const savePromise = this.trainingVerifyService.saveTrainingVerifyData(body).toPromise();
              savePromises.push(savePromise);
            }
          }
        });
      });

      if (savePromises.length === 0) {
        resolve();
        return;
      }

      Promise.all(savePromises)
        .then(() => resolve())
        .catch((err) => {
          console.error("Error saving supervisor selections", err);
          reject(err);
        });
    });
  }

  getLevelForTraineeAndTopic(traineeId: number, topicKey: string): string {
    const trainee = this.quizData.data.find(
      (t: any) => t.trainee_id === traineeId
    );
    if (!trainee) return "";

    const quizQuestion = trainee.topics?.[topicKey]?.[this.currentStage]?.[0];
    if (!quizQuestion) return "";

    return quizQuestion.level || "";
  }

  goBack(): void {
    this.router.navigate(["/dashboard"]);
  }

  getClassForTraineeAndTopic(traineeId: number, topicKey: string): string {
    // Never show trainee answers - always return empty string
    return "";
  }
}
